/* eslint-disable @typescript-eslint/no-explicit-any */
import { create } from 'zustand';

import { prepareHeaders, retryFetch } from '@app/app/lib/api';
import { serverPath } from '@app/utils/server-path';
import { FetchError, handleFetchError } from '@app/app/lib';

// Конфигурация маркера для одного уровня
// Массив объектов: первый элемент с type="marker" - это коробка маркера,
// остальные элементы - это объекты контента (path, image и т.д.)
export type StyleMarkerConfig = any[];

// Конфигурация маркеров для всех уровней одного типа стиля
export type StyleMarkersConfig = {
  [index: string]: StyleMarkerConfig; // '0' = level-1, '1' = level-2, '2' = level-3, '3' = level-4
};

// Весь ответ от API с поддержкой нескольких типов стилей
export interface StyleInfoResponse {
  result: {
    [styleType: string]: {
      markers: StyleMarkersConfig;
      theme_id?: number;
    };
  };
}

// Объект available_images из конфига канваса (для справки, не хранится в store)
export interface AvailableImage {
  image: string; // URL с {color} плейсхолдером
  preview: string;
  source_ext: string;
}

// State store
type StyleInfoStoreState = {
  // Конфиги маркеров по типам стилей и уровням
  // Структура: { "normal": { "0": [...], "1": [...] }, "adaptive": { "0": [...], "1": [...] } }
  markersByStyleType: Record<string, StyleMarkersConfig> | null;

  // Статус загрузки
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
};

// Actions
type StyleInfoStoreActions = {
  // Загрузить style-info с бэкенда
  fetchStyleInfo: (workspaceId: string) => Promise<void>;

  // Получить конфиг маркера для уровня с учетом типа стиля
  getMarkerConfigForLevel: (level: number, markerType?: string) => StyleMarkerConfig | null;

  // Очистить store
  clearStore: () => void;
};

type StyleInfoStore = StyleInfoStoreState & StyleInfoStoreActions;

const defaultState: StyleInfoStoreState = {
  markersByStyleType: null,
  isLoaded: false,
  isLoading: false,
  error: null,
};

export const useStyleInfoStore = create<StyleInfoStore>((set, get) => ({
  ...defaultState,

  fetchStyleInfo: async (workspaceId: string) => {
    // Проверяем что еще не загружено
    // const { isLoaded, isLoading } = get();
    // if (isLoaded || isLoading) {
    //   console.debug('🎨 [StyleInfo] Already loaded or loading, skipping fetch');
    //   return;
    // }

    // set({ isLoading: true, error: null });

    try {
      const url = serverPath.workspace.getStyleInfo(workspaceId);
      console.debug('🎨 [StyleInfo] Fetching style info from:', url);

      const response = await retryFetch({
        cb: async () => {
          const res = await fetch(url, {
            method: 'GET',
            headers: prepareHeaders(),
          });

          if (!res.ok) {
            throw new FetchError(`HTTP error! status: ${res.status}`, res.url, res.status);
          }

          return res.json();
        },
        retries: 3,
        delayBetween: 1000,
      });

      const data = response as StyleInfoResponse;

      // Преобразуем новую структуру в удобный формат
      const markersByStyleType: Record<string, StyleMarkersConfig> = {};
      Object.keys(data.result).forEach((styleType) => {
        if (data.result[styleType]?.markers) {
          markersByStyleType[styleType] = data.result[styleType].markers;
        }
      });

      console.debug('🎨 [StyleInfo] Loaded markers:', {
        styleTypes: Object.keys(markersByStyleType),
        totalStyleTypes: Object.keys(markersByStyleType).length,
        levelsPerType: Object.keys(markersByStyleType).map((type) => ({
          type,
          levels: Object.keys(markersByStyleType[type] || {}),
        })),
      });

      set({
        markersByStyleType,
        isLoaded: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      const errorMessage = `Failed to load style-info: ${error}`;
      console.error('❌ [StyleInfo]', errorMessage);

      set({
        isLoaded: false,
        isLoading: false,
        error: errorMessage,
      });

      handleFetchError(
        new FetchError(errorMessage, serverPath.workspace.getStyleInfo(workspaceId)),
        workspaceId,
      );
    }
  },

  getMarkerConfigForLevel: (
    level: number,
    markerType: string = 'normal',
  ): StyleMarkerConfig | null => {
    const { markersByStyleType } = get();
    if (!markersByStyleType) {
      console.warn('⚠️ [StyleInfo] Markers not loaded yet');
      return null;
    }

    // Получаем конфиг для указанного типа стиля
    const styleMarkers = markersByStyleType[markerType];
    if (!styleMarkers) {
      console.warn(`⚠️ [StyleInfo] No markers found for style type: ${markerType}`);
      // Fallback на 'normal' если указанный тип не найден
      const normalMarkers = markersByStyleType['normal'];
      if (!normalMarkers) {
        return null;
      }
      const index = level - 1;
      const indexKey = index.toString();
      const config = normalMarkers[indexKey];
      if (!config) {
        console.warn(
          `⚠️ [StyleInfo] No marker config found for level ${level} (index ${index}) in fallback 'normal'`,
        );
        return null;
      }
      return config;
    }

    // Маркеры индексируются с 0: markers[0] = level-1, markers[1] = level-2, и т.д.
    const index = level - 1;
    const indexKey = index.toString();
    const config = styleMarkers[indexKey];

    if (!config) {
      console.warn(
        `⚠️ [StyleInfo] No marker config found for level ${level} (index ${index}) in style type '${markerType}'`,
      );
      return null;
    }

    return config;
  },

  clearStore: () => {
    console.debug('🧹 [StyleInfo] Clearing store');
    set(defaultState);
  },
}));
